import {ArrowLeftIcon, Code, FileText, Lock, Unlock} from "lucide-react";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";

import {Button} from "@components/ui/button.tsx";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@components/ui/card";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@components/ui/select";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@components/ui/tabs.tsx";
import {useIntegrations} from "@hooks/use-integrations.ts";
import {useUser} from "@hooks/use-user.ts";

export function SettingsPage() {
    const navigate = useNavigate();
    const {user} = useUser();
    const isAdmin = user?.isAdmin ?? false;
    const {getIntegration, setXeroTenant, lockXeroTenant, unlockXeroTenant} = useIntegrations();
    const [tenantError, setTenantError] = useState<string | null>(null);
    const [tenantSaving, setTenantSaving] = useState(false);
    const [lockSaving, setLockSaving] = useState(false);

    const xeroIntegration = getIntegration("xero");

    useEffect(() => {
        document.title = "Settings - Pecunio Agents";
    }, []);

    return (
        <div className="space-y-6 p-6">
            <div>
                <Button
                    variant="ghost"
                    size="sm"
                    className="mb-2 -ml-2 text-muted-foreground"
                    onClick={() => navigate("/dashboard")}
                >
                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    Back to Dashboard
                </Button>
                <h1 className="text-3xl font-bold">Settings</h1>
                <p className="text-muted-foreground">
                    Manage your application settings and preferences.
                </p>
            </div>

            <div className="grid gap-6">
                <Card>
                    <CardHeader>
                        <CardTitle>General Settings</CardTitle>
                        <CardDescription>
                            Configure general application preferences.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <p className="text-muted-foreground">
                            Settings functionality coming soon...
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Integration Settings</CardTitle>
                        <CardDescription>
                            Configure each integration linked to your PecunioAI account.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Tabs defaultValue="xero" className="w-full">
                            <TabsList className="grid w-full grid-cols-4 bg-card border-1 p-0 mb-4">
                                {xeroIntegration && (
                                    <TabsTrigger value="xero" className="flex items-center gap-2 ">
                                        <FileText className="h-4 w-4"/>
                                        Xero
                                    </TabsTrigger>
                                )}
                                <TabsTrigger value="email" className="flex items-center gap-2">
                                    <Code className="h-4 w-4"/>
                                    Email
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="xero" className="">
                                <div className="flex flex-col gap-6">
                                    <CardHeader>
                                        <h3 className="text-lg">Configure your Xero connection</h3>
                                    </CardHeader>
                                    <CardContent>
                                        {!xeroIntegration?.connected && (
                                            <div>
                                                <Button className="text-white" variant="default">
                                                    <a href="/api/integrations/xero/login">Connect To Xero</a>
                                                </Button>
                                            </div>
                                        )}
                                        {xeroIntegration?.connected && (
                                            <div className="">
                                                <p className="text-sm">
                                                    Connection Status: <span className="text-success">Connected</span>
                                                </p>
                                                <p className="text-sm">
                                                    Connected Tenant: <span
                                                    className="font-medium">{(xeroIntegration.xeroTenantSelected || xeroIntegration.xeroTenantDefault).tenantName}</span>
                                                    {xeroIntegration.xeroTenantLocked && (
                                                        <Lock className="inline h-3.5 w-3.5 ml-1.5 text-muted-foreground" />
                                                    )}
                                                </p>
                                                {xeroIntegration.xeroTenantLocked ? (
                                                    <div className="mt-3 space-y-2">
                                                        <p className="text-xs text-muted-foreground">
                                                            Tenant is locked{isAdmin ? "" : " by your administrator"}. This account can only access the tenant above.
                                                        </p>
                                                        {isAdmin && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                disabled={lockSaving}
                                                                onClick={async () => {
                                                                    setTenantError(null);
                                                                    setLockSaving(true);
                                                                    try {
                                                                        await unlockXeroTenant();
                                                                    } catch (error) {
                                                                        setTenantError(error instanceof Error ? error.message : "Failed to unlock tenant");
                                                                    } finally {
                                                                        setLockSaving(false);
                                                                    }
                                                                }}
                                                            >
                                                                <Unlock className="h-3.5 w-3.5 mr-1.5" />
                                                                {lockSaving ? "Unlocking..." : "Unlock"}
                                                            </Button>
                                                        )}
                                                        {tenantError && (
                                                            <p className="text-xs text-red-500">{tenantError}</p>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <>
                                                        {xeroIntegration.xeroTenants.length > 1 && isAdmin && (
                                                            <div className="mt-3 space-y-2">
                                                                <p className="text-sm">Switch Xero Tenant</p>
                                                                <Select
                                                                    value={(xeroIntegration.xeroTenantSelected || xeroIntegration.xeroTenantDefault).tenantId}
                                                                    onValueChange={async (tenantId) => {
                                                                        setTenantError(null);
                                                                        setTenantSaving(true);
                                                                        try {
                                                                            await setXeroTenant(tenantId);
                                                                        } catch (error) {
                                                                            setTenantError(error instanceof Error ? error.message : "Failed to switch tenant");
                                                                        } finally {
                                                                            setTenantSaving(false);
                                                                        }
                                                                    }}
                                                                >
                                                                    <SelectTrigger className="max-w-sm">
                                                                        <SelectValue placeholder="Select tenant"/>
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {xeroIntegration.xeroTenants.map((tenant) => (
                                                                            <SelectItem key={tenant.tenantId} value={tenant.tenantId}>
                                                                                {tenant.tenantName}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                {tenantSaving && (
                                                                    <p className="text-xs text-muted-foreground">Switching tenant...</p>
                                                                )}
                                                                {tenantError && (
                                                                    <p className="text-xs text-red-500">{tenantError}</p>
                                                                )}
                                                            </div>
                                                        )}
                                                        {isAdmin && (
                                                            <div className="mt-3">
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    disabled={lockSaving}
                                                                    onClick={async () => {
                                                                        setTenantError(null);
                                                                        setLockSaving(true);
                                                                        try {
                                                                            await lockXeroTenant();
                                                                        } catch (error) {
                                                                            setTenantError(error instanceof Error ? error.message : "Failed to lock tenant");
                                                                        } finally {
                                                                            setLockSaving(false);
                                                                        }
                                                                    }}
                                                                >
                                                                    <Lock className="h-3.5 w-3.5 mr-1.5" />
                                                                    {lockSaving ? "Locking..." : "Lock to this tenant"}
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                                <div className="text-xs text-muted-foreground mt-2">
                                                    Connection problems? <a className="underline"
                                                    href="/api/integrations/xero/login">Reconnect</a>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </div>
                            </TabsContent>

                            <TabsContent value="email" className="">
                                <div className="flex flex-col gap-6">
                                    <CardHeader>
                                        <h3 className="text-lg">Configure your Email connection</h3>
                                    </CardHeader>
                                    <CardContent>
                                        Coming Soon...
                                    </CardContent>
                                </div>
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Notifications</CardTitle>
                        <CardDescription>
                            Manage your notification preferences.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <p className="text-muted-foreground">
                            Notification settings coming soon...
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
